// Shared helpers for the new phone-pricing shape (3-tier finance + sub-variants).

export const LABELS = {
  finance: "Finance",
  switch: "Switch",
  join: "Join",
  no_hassle: "No Hassle",
  full_retail: "Full Retail",
  single_month: "Single month",
  multi_month: "Multi month",
  multi_line: "Multi line",
};

const SC_GROUPS = ["switch", "join", "no_hassle"];
const SUB_KEYS = ["single_month", "multi_month", "multi_line"];
// Group priority — when a customer qualifies for multiple acquisition paths, Switch
// wins over Join wins over No Hassle (matches the dealer's preferred funnel).
const GROUP_PRIORITY = { switch: 3, join: 2, no_hassle: 1, full_retail: 0, finance: 0 };
// Sub-variant priority — used only as a tiebreaker within the same group.
// Broader qualification wins: multi_line > multi_month > single_month.
const SUB_PRIORITY = { multi_line: 3, multi_month: 2, single_month: 1 };

function financeMonthly(f, price) {
  const m = Math.max(1, parseInt(f.months || 24, 10));
  return Math.max(0, ((price || 0) - (f.fcot_credit || 0)) / m);
}

/**
 * Enumerate every enabled option from a PhonePricing object.
 * Returns a flat list — each entry has:
 *   key: stable identifier ("finance" | "full_retail" | "<group>.<sub>")
 *   label: human-friendly name
 *   group: "finance" | "switch" | "join" | "no_hassle" | "full_retail"
 *   sub: null | "single_month" | "multi_month" | "multi_line"
 *   data: the underlying scenario object
 *   priceForRecommend: representative single-number price used for sorting/comparison
 *   financeTiers (finance only): [{tier, price (monthly)}] for the 3 credit tiers
 *   isFinance: bool
 */
export function enumerateOptions(pricing) {
  if (!pricing) return [];
  const out = [];

  if (pricing.finance?.enabled) {
    const f = pricing.finance;
    const tiers = [1, 2, 3]
      .map((n) => {
        const price = f[`device_price_tier${n}`] || 0;
        return price > 0 ? { tier: n, price: financeMonthly(f, price), devicePrice: price } : null;
      })
      .filter(Boolean);
    out.push({
      key: "finance",
      label: LABELS.finance,
      group: "finance",
      sub: null,
      data: f,
      financeTiers: tiers,
      // Headline = lowest monthly (best-case) for sort/comparison
      priceForRecommend: tiers.length ? Math.min(...tiers.map((t) => t.price)) : 0,
      isFinance: true,
      groupPriority: GROUP_PRIORITY.finance,
      priority: 0,
    });
  }

  for (const sc of SC_GROUPS) {
    for (const sub of SUB_KEYS) {
      const v = pricing[sc]?.[sub];
      if (v?.enabled) {
        out.push({
          key: `${sc}.${sub}`,
          // single_month variants display as just the group name (no "· Single month" suffix).
          label: sub === "single_month" ? LABELS[sc] : `${LABELS[sc]} · ${LABELS[sub]}`,
          group: sc,
          sub,
          data: v,
          priceForRecommend: v.device_price || 0,
          isFinance: false,
          groupPriority: GROUP_PRIORITY[sc] || 0,
          priority: SUB_PRIORITY[sub] || 0,
        });
      }
    }
  }

  if (pricing.full_retail?.enabled) {
    out.push({
      key: "full_retail",
      label: LABELS.full_retail,
      group: "full_retail",
      sub: null,
      data: pricing.full_retail,
      priceForRecommend: pricing.full_retail.price || 0,
      isFinance: false,
      groupPriority: GROUP_PRIORITY.full_retail,
      priority: 0,
    });
  }

  return out;
}

/**
 * Evaluate whether the customer qualifies for an option.
 * answers = { hasId, porting, fromCarrierId, fromCarrierEligible, planId, lines }
 * `fromCarrierEligible` = boolean from Carrier.is_port_in_eligible (looked up by the caller).
 */
export function evaluateOption(opt, answers = {}) {
  if (!opt) return { ok: false, reason: "No option" };
  const { hasId, porting, fromCarrierId, fromCarrierEligible, planId, lines } = answers;

  if (opt.isFinance) {
    // Finance also respects qualifying-plan restrictions when a plan has been picked.
    const elig = opt.data.eligible_plan_ids || [];
    if (planId && elig.length > 0 && !elig.includes(planId)) {
      return { ok: false, reason: "Plan not on the finance-qualifying list" };
    }
    return { ok: true };
  }

  const s = opt.data;

  // Verify ID
  if (s.verify_id === "required" && hasId === false) {
    return { ok: false, reason: "Requires valid ID" };
  }
  // Port-in — carrier eligibility is now driven by Carrier.is_port_in_eligible (global flag),
  // not by a per-promo carrier list.
  if (s.port_in === "required") {
    if (porting !== true) return { ok: false, reason: "Requires porting from another carrier" };
    if (!fromCarrierId) return { ok: false, reason: "Pick the carrier you're porting from" };
    if (fromCarrierEligible === false) {
      return { ok: false, reason: "Selected carrier isn't eligible for port-in promos" };
    }
  } else if (s.port_in === "optional" && porting === true) {
    if (fromCarrierId && fromCarrierEligible === false) {
      return { ok: false, reason: "Selected carrier isn't eligible for port-in promos" };
    }
  }
  // Multi-line phone promos: customer qualifies when either
  //   - they have ≥ required_devices phone lines, OR
  //   - the combined count of phone + tablet lines meets/exceeds required_devices
  //     (so 1 phone + 1 tablet satisfies a 2-device multi_line promo).
  // `qualifyingDeviceCount` is provided by the Bundle Builder (phones + tablets). When
  // omitted (e.g. DeviceDetail deal builder), we fall back to `lines`.
  if (opt.sub === "multi_line" && (opt.data.required_devices || 0) > 0) {
    const count = answers.qualifyingDeviceCount != null ? answers.qualifyingDeviceCount : (lines || 0);
    if (count < opt.data.required_devices) {
      return { ok: false, reason: `Multi-line requires ${opt.data.required_devices}+ devices (phones + tablets combined)` };
    }
  }
  void lines;
  // Plan eligibility (when a plan has been picked)
  if (planId && (s.eligible_plan_ids || []).length > 0 && !s.eligible_plan_ids.includes(planId)) {
    return { ok: false, reason: "Plan not on the qualifying list" };
  }
  return { ok: true };
}

/**
 * Compute the service-commitment for a multi-month or multi-line sub-variant.
 * Line-months are fungible: total = required_X line-months, paid for as floor(required_X / lines)
 * full months at L-line cost, plus a partial month for the remainder using the tier price for
 * (required_X mod lines) lines.
 *
 *   calcLineSubtotal(plan, n)  →  per-month total cost of n lines (respects tier pricing)
 *
 * Returns `null` when this option has no service commitment (single_month / finance / full_retail).
 */
export function computeCommitment(option, plan, lines, calcLineSubtotal) {
  if (!option || !plan || !lines || option.isFinance || option.group === "full_retail") return null;
  if (option.sub !== "multi_month" && option.sub !== "multi_line") return null;
  const required = option.sub === "multi_month"
    ? (option.data.required_months || 0)
    : (option.data.required_devices || 0);
  if (required <= 0) return null;

  const fullMonths = Math.floor(required / lines);
  const remainderLines = required - fullMonths * lines;
  const cost = fullMonths * calcLineSubtotal(plan, lines)
    + (remainderLines > 0 ? calcLineSubtotal(plan, remainderLines) : 0);
  const monthsToComplete = fullMonths + (remainderLines > 0 ? 1 : 0);
  return {
    lineMonthsRequired: required,
    monthsToComplete,
    fullMonthLines: lines,
    remainderLines,
    commitmentCost: cost,
  };
}

/** True iff this option has any phone-pricing scenario enabled. */
export function hasAnyEnabled(pricing) {
  return enumerateOptions(pricing).length > 0;
}

/** Helper: minimum option price across all enabled options (for "from $X" badges). */
export function minOptionPrice(pricing) {
  const opts = enumerateOptions(pricing);
  if (!opts.length) return 0;
  return Math.min(...opts.map((o) => o.priceForRecommend || 0));
}
